// ─── ROTATION MAP ─────────────────────────────────────────────────────────────
// Where each fund sits against a benchmark: X = relative strength against its own
// recent norm, Y = the momentum of that relative strength, both centred on 100.
// The formula and the universe live in the backend (app/services/rotation.py);
// this view only draws GET /api/rotation.
//
// ⚠ Hand-written SVG with d3 scales — NOT recharts. The tails, quadrant shading,
// head labels and their collision handling are plain geometry here, so they can
// be asserted from renderToString (harness/rotation_render.mjs); recharts draws
// nothing measurable outside a browser and owns its own hover logic.
// ⚠ One request per benchmark/period: all groups at the longest tail. The tail
// slider, group toggles, search and isolation filter that answer locally.
// ⚠⚠ UNAVAILABLE MEANS NOTHING ELSE. A failed or "available: false" answer shows
// the one line below — no previous points kept, no placeholder map. A switch of
// benchmark or period clears the drawn map before the new request.
// ⚠ Colours: C.amber is emerald in this palette, so weakening uses C.orange.
// Colour is the quadrant; the head marker's shape is the group.
// ⚠ Layout: the plot takes the full tab width and the height left in the
// viewport; at WIDE_MIN and above the reading list sits beside it, below that it
// sits under the plot. The phone layout (square plot, list below) is unchanged.
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { scaleLinear } from "d3";
import { C, s, font, backendFetch } from "./core";
import { SearchBox } from "./components/SearchBox";

export const GROUPS = [
  { key: "sectors", label: "SECTORS", shape: "circle" },
  { key: "themes", label: "THEMES", shape: "square" },
  { key: "size", label: "SIZE & STYLE", shape: "diamond" },
  { key: "cross", label: "CROSS-ASSET", shape: "triangle" },
];
const SHAPE = Object.fromEntries(GROUPS.map((g) => [g.key, g.shape]));
export const DEFAULT_GROUPS = ["sectors"];      // themes and size one click away; cross-asset is opt-in
export const TAIL_MIN = 3;
export const TAIL_MAX = 15;
export const TAIL_DEFAULT = 5;
export const QUAD = {
  leading: { label: "LEADING", color: C.green },
  weakening: { label: "WEAKENING", color: C.orange },
  lagging: { label: "LAGGING", color: C.red },
  improving: { label: "IMPROVING", color: C.blue },
};
const QUAD_ORDER = ["leading", "weakening", "lagging", "improving"];
export const UNAVAILABLE_TEXT = "Rotation data unavailable";
const RSP_NOTE = "RSP weighs every S&P 500 stock equally: the largest companies stop dominating the benchmark, and RSP leaves the map.";
const DRIFT_NOTE = "Persistent winners drift back toward the centre: momentum fades while a lead holds, and the lead fades once it stops growing.";
export const PHONE_MAX_W = 600;
export const WIDE_MIN = 1000;          // tab width at which the reading list moves beside the plot
export const PANEL_W = 320;
export const CROWDED = 12;             // above this many items, colliding labels are dropped
export const LABEL_MODES = ["all", "selected", "none"];
export const FOCUS_DIM = 0.1;          // every other item while one is hovered or pinned
const SEARCH_DIM = 0.18;
const PLOT_FALLBACK = 560;
const WIDTH_FALLBACK = 960;
const PLOT_MIN_H = 360;

const fin = (v) => typeof v === "number" && Number.isFinite(v);

// ── data ─────────────────────────────────────────────────────────────────────
// A usable answer, or null. Items with any non-finite point are dropped whole —
// a partial tail is never drawn as if it were the series.
export function sanitize(body) {
  if (!body || body.available !== true || !Array.isArray(body.items)) return null;
  const items = body.items.filter((it) => it && typeof it.symbol === "string"
    && QUAD[it.quadrant] && Array.isArray(it.points) && it.points.length > 0
    && it.points.every((p) => p && fin(p.x) && fin(p.y)));
  return { ...body, items, missing: Array.isArray(body.missing) ? body.missing : [] };
}

export async function fetchRotation(benchmark, period) {
  const q = `benchmark=${benchmark}&period=${period}&tail=${TAIL_MAX}&groups=${GROUPS.map((g) => g.key).join(",")}`;
  const res = await backendFetch(null, `/api/rotation?${q}`);
  if (!res || !res.ok) return null;
  return sanitize(await res.json());
}

export function matchesQuery(it, q) {
  const t = String(q || "").trim();
  if (!t) return true;
  return it.symbol.includes(t.toUpperCase()) || String(it.name || "").toLowerCase().includes(t.toLowerCase());
}

// The items drawn for the current controls. The benchmark is never an item.
export function visibleItems(data, { groups, isolated, query, isolateSearch }) {
  if (!data) return [];
  return data.items.filter((it) => it.symbol !== data.benchmark
    && groups.includes(it.group)
    && (!isolated || it.symbol === isolated)
    && (!isolateSearch || matchesQuery(it, query)));
}

// Direction of the last step of the visible tail, in degrees (0 = right, 90 = up),
// or null when there is no step.
export function trendAngle(points, tail) {
  const pts = points.slice(-tail);
  if (pts.length < 2) return null;
  const a = pts[pts.length - 2], b = pts[pts.length - 1];
  const dx = b.x - a.x, dy = b.y - a.y;
  if (dx === 0 && dy === 0) return null;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

// ── geometry ─────────────────────────────────────────────────────────────────
const PAD = { l: 30, r: 10, t: 10, b: 26 };

// Label boxes are estimated from the character count (monospace font).
// `force` labels (what the user picked) are placed first and always drawn. With
// hideCollided, any other label that would sit on a label or off the plot is
// dropped instead of drawn on top.
export function placeLabels(heads, { w, h, fs, hideCollided = false, obstacles: obs = heads }) {
  const cw = fs * 0.62, lh = fs + 2;
  const placed = [];
  const obstacles = obs.map((hd) => ({ x0: hd.x - 5, y0: hd.y - 5, x1: hd.x + 5, y1: hd.y + 5 }));
  const area = (a, b) => Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0))
    * Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
  const out = {};
  const order = [...heads].sort((a, b) => (b.force ? 1 : 0) - (a.force ? 1 : 0) || a.y - b.y || a.x - b.x);
  for (const hd of order) {
    const lw = hd.text.length * cw;
    const cands = [
      [hd.x + 8, hd.y - lh / 2], [hd.x - 8 - lw, hd.y - lh / 2],
      [hd.x - lw / 2, hd.y - 8 - lh], [hd.x - lw / 2, hd.y + 8],
      [hd.x + 6, hd.y - 6 - lh], [hd.x + 6, hd.y + 6],
      [hd.x - 6 - lw, hd.y - 6 - lh], [hd.x - 6 - lw, hd.y + 6],
      [hd.x + 8, hd.y - lh * 1.6], [hd.x + 8, hd.y + lh * 0.6],
      [hd.x - 8 - lw, hd.y - lh * 1.6], [hd.x - 8 - lw, hd.y + lh * 0.6],
      [hd.x - lw / 2, hd.y - 8 - lh * 2], [hd.x - lw / 2, hd.y + 8 + lh],
      [hd.x + 8, hd.y - lh * 2.6], [hd.x + 8, hd.y + lh * 1.6],
      [hd.x - 8 - lw, hd.y - lh * 2.6], [hd.x - 8 - lw, hd.y + lh * 1.6],
    ];
    // A label on a label is unreadable; a label over another head's marker is
    // only untidy — so label overlap costs ten times as much.
    let best = null;
    for (const [x0, y0] of cands) {
      const b = { x0, y0, x1: x0 + lw, y1: y0 + lh };
      const outside = b.x0 < 0 || b.y0 < 0 || b.x1 > w || b.y1 > h;
      let clash = 0, cost = outside ? 1e6 : 0;
      for (const p of placed) clash += area(b, p);
      cost += 10 * clash;
      for (const o of obstacles) cost += area(b, o);
      if (!best || cost < best.cost) best = { b, cost, clash, outside };
      if (cost === 0) break;
    }
    if (hideCollided && !hd.force && (best.clash > 0 || best.outside)) continue;
    placed.push(best.b);
    out[hd.symbol] = { x: best.b.x0, y: best.b.y0 + fs, box: best.b };
  }
  return out;
}

// picked: symbols the user selected, searched or is hovering — labelled with the
// name on desktop and always drawn. labelMode decides the rest: "all" (ticker
// only), "selected" or "none" (no label).
export function layoutMap(items, { w, h, tail, phone, picked = null, labelMode = "all" }) {
  const series = items.map((it) => ({ ...it, pts: it.points.slice(-tail) }));
  let rx = 0.5, ry = 0.5;
  for (const it of series) for (const p of it.pts) {
    rx = Math.max(rx, Math.abs(p.x - 100));
    ry = Math.max(ry, Math.abs(p.y - 100));
  }
  rx *= 1.12; ry *= 1.12;
  const xs = scaleLinear().domain([100 - rx, 100 + rx]).range([PAD.l, w - PAD.r]);
  const ys = scaleLinear().domain([100 - ry, 100 + ry]).range([h - PAD.b, PAD.t]);
  const fs = phone ? 10 : 11;
  const drawn = series.map((it) => {
    const px = it.pts.map((p) => ({ x: xs(p.x), y: ys(p.y), date: p.date }));
    const n = px.length;
    const segs = [];
    // Strong fade: the oldest step is nearly transparent, the newest full.
    for (let k = 1; k < n; k++) {
      segs.push({ x1: px[k - 1].x, y1: px[k - 1].y, x2: px[k].x, y2: px[k].y,
        opacity: n > 1 ? 0.04 + 0.96 * Math.pow(k / (n - 1), 1.6) : 1 });
    }
    const head = px[n - 1];
    let arrow = null;
    if (n > 1) {
      const dx = head.x - px[n - 2].x, dy = head.y - px[n - 2].y;
      const len = Math.hypot(dx, dy);
      if (len > 0.5) {
        const ux = dx / len, uy = dy / len, bx = head.x - ux * 11, by = head.y - uy * 11;
        arrow = [[head.x, head.y], [bx - uy * 5, by + ux * 5], [bx + uy * 5, by - ux * 5]];
      }
    }
    const isPicked = !!picked?.has(it.symbol);
    return { ...it, px, segs, head, arrow, color: QUAD[it.quadrant].color, picked: isPicked,
      shape: SHAPE[it.group] || "circle", text: !phone && isPicked ? `${it.symbol} ${it.name}` : it.symbol };
  });
  const want = drawn.filter((d) => d.picked || labelMode === "all");
  const labels = placeLabels(want.map((d) => ({ symbol: d.symbol, x: d.head.x, y: d.head.y, text: d.text, force: d.picked })),
    { w, h, fs, hideCollided: drawn.length > CROWDED, obstacles: drawn.map((d) => d.head) });
  return { w, h, xs, ys, cx: xs(100), cy: ys(100), drawn, labels, fs, rx, ry };
}

// ── view helpers ─────────────────────────────────────────────────────────────
export function isPhone() {
  if (typeof window === "undefined") return false;
  if (Number.isFinite(window.innerWidth) && window.innerWidth < PHONE_MAX_W) return true;
  try { return !!window.matchMedia?.("(pointer: coarse)")?.matches; } catch { return false; }
}

const _asOfFmt = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
export function asOfLabel(d) {
  const ms = Date.parse(`${String(d || "").slice(0, 10)}T00:00:00Z`);
  return Number.isFinite(ms) ? `As of ${_asOfFmt.format(ms)} close` : null;
}
const fmt2 = (v) => v.toFixed(2);
const unit = (period, n) => `${n} ${period === "weekly" ? (n === 1 ? "week" : "weeks") : (n === 1 ? "day" : "days")}`;

// A group marker centred on (x, y).
function marker(shape, x, y, r, props) {
  if (shape === "square") {
    const a = r * 0.88;
    return <rect x={x - a} y={y - a} width={2 * a} height={2 * a} {...props} />;
  }
  if (shape === "diamond") {
    const a = r * 1.25;
    return <polygon points={`${x},${y - a} ${x + a},${y} ${x},${y + a} ${x - a},${y}`} {...props} />;
  }
  if (shape === "triangle") {
    const a = r * 1.3;
    return <polygon points={`${x},${y - a} ${x + a * 0.87},${y + a * 0.5} ${x - a * 0.87},${y + a * 0.5}`} {...props} />;
  }
  return <circle cx={x} cy={y} r={r} {...props} />;
}

function ShapeIcon({ shape, color }) {
  return (
    <svg width={10} height={10} viewBox="-5 -5 10 10" aria-hidden="true" style={{ flex: "0 0 auto" }}>
      {marker(shape, 0, 0, 3.6, { fill: color })}
    </svg>
  );
}

function TrendArrow({ angle, color }) {
  if (angle == null) return <span style={{ width: 12, flex: "0 0 12px" }} />;
  return (
    <svg data-trend={Math.round(angle)} width={12} height={12} viewBox="0 0 12 12" aria-hidden="true" style={{ flex: "0 0 12px" }}>
      <g transform={`rotate(${-angle} 6 6)`}>
        <path d="M1.5 6 H10 M7 3 L10 6 L7 9" fill="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

function Toggle({ on, onClick, children, title, compact }) {
  return (
    <button onClick={onClick} title={title} aria-pressed={on} style={{
      ...s.btn(on ? "amber" : "ghost"), minHeight: compact ? 30 : 36, padding: compact ? "4px 8px" : "6px 10px",
      fontSize: 11, opacity: on ? 1 : 0.75,
    }}>{children}</button>
  );
}

const Divider = () => <span aria-hidden="true" style={{ width: 1, alignSelf: "stretch", background: C.border, margin: "0 4px" }} />;
const BarLabel = ({ children }) => <span style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.1em" }}>{children}</span>;

// Placed below the head and its label (above them when there is no room), so
// the card never covers the item it describes.
const CARD_W = 210, CARD_H = 96;
function HoverCard({ item, period, at, label, w, h }) {
  const cw = CARD_W;
  const left = Math.max(0, Math.min(at.x - 20, w - cw));
  const below = Math.max(at.y + 12, label ? label.y1 + 6 : 0);
  const above = Math.min(at.y - 12, label ? label.y0 - 6 : Infinity) - CARD_H;
  const top = Math.max(0, below + CARD_H <= h || above < 0 ? below : above);
  const q = QUAD[item.quadrant];
  const last = item.points[item.points.length - 1];
  return (
    <div data-card={item.symbol} style={{
      position: "absolute", left, top, width: cw, boxSizing: "border-box", zIndex: 2,
      background: C.panelAlt, border: `1px solid ${C.borderHi}`, borderRadius: 3,
      padding: "8px 10px", fontSize: 11, color: C.text, fontFamily: font, pointerEvents: "auto",
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{item.name} <span style={{ color: C.textMid }}>{item.symbol}</span></div>
      <div>X (relative strength) <b>{fmt2(last.x)}</b></div>
      <div>Y (momentum) <b>{fmt2(last.y)}</b></div>
      <div style={{ color: q.color, fontWeight: 700, marginTop: 4 }}>{q.label}</div>
      {Number.isInteger(item.periods_in_quadrant) && item.periods_in_quadrant > 0 ? (
        <div style={{ color: C.textMid }}>{unit(period, item.periods_in_quadrant)} in quadrant</div>
      ) : null}
    </div>
  );
}

// The plot itself. Pure render from props, so the harness can drive it directly.
// `size` is a square shorthand for w = h.
export function RotationPlot({ items, period, tail, size, w: wIn, h: hIn, phone, highlight, selected, labelMode = "all",
  hover, pinned, onHover, onPin }) {
  const w = wIn ?? size, h = hIn ?? size;
  const focus = pinned || hover || null;
  const picked = useMemo(() => {
    const p = new Set(selected || []);
    if (focus) p.add(focus);
    return p;
  }, [selected, focus]);
  const L = useMemo(() => layoutMap(items, { w, h, tail, phone, picked, labelMode }), [items, w, h, tail, phone, picked, labelMode]);
  const { xs, ys, cx, cy } = L;
  const x0 = PAD.l, x1 = w - PAD.r, y0 = PAD.t, y1 = h - PAD.b;
  const quads = [
    { k: "leading", x: cx, y: y0, w: x1 - cx, h: cy - y0, lx: x1 - 6, ly: y0 + 14, anchor: "end" },
    { k: "weakening", x: cx, y: cy, w: x1 - cx, h: y1 - cy, lx: x1 - 6, ly: y1 - 6, anchor: "end" },
    { k: "lagging", x: x0, y: cy, w: cx - x0, h: y1 - cy, lx: x0 + 6, ly: y1 - 6, anchor: "start" },
    { k: "improving", x: x0, y: y0, w: cx - x0, h: cy - y0, lx: x0 + 6, ly: y0 + 14, anchor: "start" },
  ];
  const focusItem = focus ? L.drawn.find((d) => d.symbol === focus) : null;
  const xt = xs.ticks(w > 700 ? 8 : 5), yt = ys.ticks(h > 500 ? 7 : 5);
  // Faint items first, the focused one last, so it is drawn on top.
  const rank = (d) => (d.symbol === focus ? 2 : highlight?.has(d.symbol) ? 1 : 0);
  const order = [...L.drawn].sort((a, b) => rank(a) - rank(b));
  return (
    <div style={{ position: "relative", width: w, maxWidth: "100%" }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Rotation map"
           style={{ display: "block", fontFamily: font, touchAction: "manipulation" }}>
        {quads.map((q) => (
          <g key={q.k}>
            <rect data-quad={q.k} x={q.x} y={q.y} width={Math.max(0, q.w)} height={Math.max(0, q.h)}
                  fill={QUAD[q.k].color} fillOpacity={0.07} />
            <text x={q.lx} y={q.ly} textAnchor={q.anchor} fontSize={10} fill={QUAD[q.k].color}
                  fillOpacity={0.8} letterSpacing="0.1em">{QUAD[q.k].label}</text>
          </g>
        ))}
        <line x1={cx} x2={cx} y1={y0} y2={y1} stroke={C.borderHi} />
        <line x1={x0} x2={x1} y1={cy} y2={cy} stroke={C.borderHi} />
        {xt.map((t) => (
          <text key={`x${t}`} x={xs(t)} y={h - 12} textAnchor="middle" fontSize={9} fill={C.textDim}>{t}</text>
        ))}
        {yt.map((t) => (
          <text key={`y${t}`} x={x0 - 4} y={ys(t) + 3} textAnchor="end" fontSize={9} fill={C.textDim}>{t}</text>
        ))}
        <text x={x1} y={h - 2} textAnchor="end" fontSize={9} fill={C.textMid}>RELATIVE STRENGTH →</text>
        <text x={x0} y={y0 - 2} fontSize={9} fill={C.textMid} transform={`rotate(-90 ${x0 - 20} ${y0 + 4})`}
              textAnchor="end">MOMENTUM →</text>
        {order.map((d) => {
          const isFocus = d.symbol === focus;
          const op = focus ? (isFocus ? 1 : FOCUS_DIM) : highlight && !highlight.has(d.symbol) ? SEARCH_DIM : 1;
          const sw = isFocus ? 2.6 : 1.3;
          const lab = L.labels[d.symbol];
          return (
            <g key={d.symbol} data-item={d.symbol} data-quadrant={d.quadrant} data-group={d.group} opacity={op}>
              {d.segs.map((sg, k) => (
                <polyline key={k} data-seg={k} points={`${sg.x1},${sg.y1} ${sg.x2},${sg.y2}`} fill="none"
                          stroke={d.color} strokeWidth={sw} strokeOpacity={sg.opacity} strokeLinecap="round" />
              ))}
              {d.px.slice(0, -1).map((p, k) => (
                <circle key={k} cx={p.x} cy={p.y} r={isFocus ? 2 : 1.3} fill={d.color}
                        fillOpacity={d.segs[k] ? d.segs[k].opacity : 1} />
              ))}
              {d.arrow ? (
                <polygon data-arrow={d.symbol} points={d.arrow.map((p) => p.join(",")).join(" ")} fill={d.color} />
              ) : null}
              {marker(d.shape, d.head.x, d.head.y, isFocus ? 5 : phone ? 4.5 : 4, {
                "data-marker": d.symbol, "data-shape": d.shape, fill: d.color, stroke: C.bg, strokeWidth: 1.2,
              })}
              {lab ? (
                <text data-label={d.symbol} x={lab.x} y={lab.y} fontSize={L.fs} fill={isFocus ? C.text : d.color}
                      fontWeight={700} stroke={C.bg} strokeWidth={3} strokeOpacity={0.85} paintOrder="stroke"
                      strokeLinejoin="round">{d.text}</text>
              ) : null}
              <circle data-head={d.symbol} cx={d.head.x} cy={d.head.y} r={phone ? 14 : 10} fill="transparent"
                      style={{ cursor: "pointer" }}
                      onPointerEnter={(e) => { if (e.pointerType !== "touch") onHover?.(d.symbol); }}
                      onPointerLeave={(e) => { if (e.pointerType !== "touch") onHover?.(null); }}
                      onPointerDown={() => onPin?.(d.symbol)} />
            </g>
          );
        })}
      </svg>
      {focusItem ? <HoverCard item={focusItem} period={period} at={focusItem.head} label={L.labels[focusItem.symbol]?.box}
                              w={w} h={h} /> : null}
    </div>
  );
}

// The list by quadrant, beside the plot on wide screens and under it otherwise.
// Hovering a row focuses that item on the plot; a click isolates it.
export function ReadingList({ rows, period, tail, focus, isolated, onHover, onIsolate }) {
  return (
    <div data-reading>
      {QUAD_ORDER.map((qk) => {
        const rs = rows.filter((it) => it.quadrant === qk)
          .sort((a, b) => b.points[b.points.length - 1].x - a.points[a.points.length - 1].x);
        if (!rs.length) return null;
        return (
          <div key={qk} data-list={qk} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 11, marginBottom: 2,
              borderBottom: `1px solid ${C.borderHi}`, paddingBottom: 3 }}>
              <span style={{ fontWeight: 700, color: QUAD[qk].color, letterSpacing: "0.1em", flex: 1 }}>
                {QUAD[qk].label} <span style={{ color: C.textDim, fontWeight: 400 }}>{rs.length}</span>
              </span>
              <span style={{ width: 52, textAlign: "right", color: C.textDim, fontSize: 10 }}>X</span>
              <span style={{ width: 52, textAlign: "right", color: C.textDim, fontSize: 10 }}>Y</span>
            </div>
            {rs.map((it) => {
              const last = it.points[it.points.length - 1];
              const on = focus === it.symbol || isolated === it.symbol;
              return (
                <button key={it.symbol} data-row={it.symbol} aria-pressed={isolated === it.symbol}
                        title={`${it.name} — click to isolate`}
                        onPointerEnter={(e) => { if (e.pointerType !== "touch") onHover?.(it.symbol); }}
                        onPointerLeave={(e) => { if (e.pointerType !== "touch") onHover?.(null); }}
                        onClick={() => onIsolate(it.symbol)} style={{
                  display: "flex", width: "100%", minHeight: 28, gap: 6, alignItems: "center",
                  background: on ? C.panel : "none", border: "none", borderBottom: `1px solid ${C.border}`,
                  color: C.text, fontFamily: font, fontSize: 11, textAlign: "left", padding: "3px 2px", cursor: "pointer",
                }}>
                  <TrendArrow angle={trendAngle(it.points, tail)} color={QUAD[it.quadrant].color} />
                  <ShapeIcon shape={SHAPE[it.group] || "circle"} color={QUAD[it.quadrant].color} />
                  <span style={{ fontWeight: 700, minWidth: 38 }}>{it.symbol}</span>
                  <span style={{ flex: 1, minWidth: 0, color: C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {it.name}
                  </span>
                  <span data-x style={{ width: 52, textAlign: "right" }}>{fmt2(last.x)}</span>
                  <span data-y style={{ width: 52, textAlign: "right" }}>{fmt2(last.y)}</span>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── tab ──────────────────────────────────────────────────────────────────────
// Width of the tab and the height left in the viewport below the plot's top.
// ⚠ Callback refs, not useRef: the plot box mounts only once data arrives, and an
// effect keyed on a ref object never re-runs for it.
function useFrame() {
  const [outer, setOuter] = useState(null);
  const [plotEl, setPlotEl] = useState(null);
  const [frame, setFrame] = useState({ w: 0, avail: 0 });
  useEffect(() => {
    if (!outer) return undefined;
    const read = () => {
      const w = outer.clientWidth || 0;
      let avail = 0;
      const vh = window.innerHeight || 0;
      if (vh) {
        const el = plotEl || outer;
        // s.main is the scroll container: measure from its top, not the scrolled position.
        let sp = el.parentElement, scrolled = window.scrollY || 0;
        while (sp) {
          const oy = window.getComputedStyle?.(sp)?.overflowY;
          if (oy === "auto" || oy === "scroll") { scrolled = sp.scrollTop; break; }
          sp = sp.parentElement;
        }
        const top = (el.getBoundingClientRect?.().top || 0) + scrolled;
        avail = Math.floor(vh - top - 24);
      }
      setFrame((f) => (f.w === w && f.avail === avail ? f : { w, avail }));
    };
    read();
    window.addEventListener?.("resize", read);
    let ro = null;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(read); ro.observe(outer); }
    return () => { window.removeEventListener?.("resize", read); ro?.disconnect(); };
  }, [outer, plotEl]);
  return { frame, setOuter, setPlotEl };
}

export function plotBox({ width, avail, phone }) {
  if (phone) {
    const side = Math.floor(width > 0 ? width : PLOT_FALLBACK);
    return { wide: false, w: side, h: side };
  }
  const W = width > 0 ? width : WIDTH_FALLBACK;
  const wide = W >= WIDE_MIN;
  const w = Math.floor(wide ? W - PANEL_W - 16 : W);
  // As tall as the viewport allows, never taller than wide.
  const h = Math.floor(Math.min(w, Math.max(PLOT_MIN_H, avail > 0 ? avail : w)));
  return { wide, w, h };
}

export function RotationMapView({ status, data, period, benchmark, onBenchmark, onPeriod, initialGroups = DEFAULT_GROUPS }) {
  const [tail, setTail] = useState(TAIL_DEFAULT);
  const [groups, setGroups] = useState(initialGroups);
  const [query, setQuery] = useState("");
  const [isolateSearch, setIsolateSearch] = useState(false);
  const [isolated, setIsolated] = useState(null);
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [labelMode, setLabelMode] = useState(null);    // null: chosen by how crowded the map is
  const [phone, setPhone] = useState(isPhone);
  const { frame, setOuter, setPlotEl } = useFrame();
  const box = plotBox({ width: frame.w, avail: frame.avail, phone });

  useEffect(() => {
    const on = () => setPhone(isPhone());
    window.addEventListener?.("resize", on);
    return () => window.removeEventListener?.("resize", on);
  }, []);
  // A pinned card is dismissed by a tap anywhere outside a head or the card
  // (the Replay pattern: capture phase, so it runs before the page handles it).
  useEffect(() => {
    if (!pinned) return undefined;
    const onDown = (e) => {
      if (!e.target?.closest?.("[data-head]") && !e.target?.closest?.("[data-card]")) setPinned(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [pinned]);

  const items = useMemo(() => visibleItems(data, { groups, isolated, query, isolateSearch }),
    [data, groups, isolated, query, isolateSearch]);
  const matches = useMemo(() => (query.trim() ? items.filter((it) => matchesQuery(it, query)).map((it) => it.symbol) : []),
    [items, query]);
  const highlight = useMemo(() => (query.trim() && !isolateSearch ? new Set(matches) : null), [matches, query, isolateSearch]);
  const selected = useMemo(() => new Set([...matches, ...(isolated ? [isolated] : [])]), [matches, isolated]);
  const mode = labelMode || (items.length > CROWDED ? "selected" : "all");
  const onPin = useCallback((sym) => setPinned((p) => (p === sym ? null : sym)), []);
  const toggleGroup = (k) => setGroups((g) => (g.includes(k) ? g.filter((x) => x !== k) : [...g, k]));
  const toggleIsolate = (sym) => { setIsolated((v) => (v === sym ? null : sym)); setPinned(null); };
  const clearAll = () => { setIsolated(null); setPinned(null); setHover(null); setQuery(""); setIsolateSearch(false); };
  const active = !!(isolated || pinned || query || isolateSearch);

  if (status === "unavailable") {
    return <div style={{ padding: 16, color: C.textMid, fontSize: 13 }}>{UNAVAILABLE_TEXT}</div>;
  }
  const asOf = data ? asOfLabel(data.as_of) : null;
  const missing = data ? data.missing.filter((m) => m && m.symbol !== data.benchmark && groups.includes(m.group)) : [];
  const legendGroups = GROUPS.filter((g) => groups.includes(g.key));
  const all = data ? data.items.filter((it) => it.symbol !== data.benchmark && groups.includes(it.group)) : [];
  const compact = !phone;
  const focus = pinned || hover;
  const list = (
    <ReadingList rows={all} period={period} tail={tail} focus={focus} isolated={isolated}
                 onHover={setHover} onIsolate={toggleIsolate} />
  );

  return (
    <div ref={setOuter}>
      <div style={{ marginBottom: 8, display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.amber, letterSpacing: "0.08em" }}>ROTATION MAP</div>
        <div style={{ fontSize: 11, color: C.textDim }}>
          Relative strength (→) and its momentum (↑) against {benchmark}, centred on 100
          {asOf ? <span> · {asOf}</span> : null}
        </div>
      </div>

      <div data-controls style={{ ...s.panel, padding: "6px 8px", marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <BarLabel>BENCHMARK</BarLabel>
          <Toggle compact={compact} on={benchmark === "SPY"} onClick={() => onBenchmark("SPY")}>SPY</Toggle>
          <Toggle compact={compact} on={benchmark === "RSP"} onClick={() => onBenchmark("RSP")} title={RSP_NOTE}>RSP</Toggle>
          <Divider />
          <Toggle compact={compact} on={period === "daily"} onClick={() => onPeriod("daily")}>DAILY</Toggle>
          <Toggle compact={compact} on={period === "weekly"} onClick={() => onPeriod("weekly")}>WEEKLY</Toggle>
          <Divider />
          {GROUPS.map((g) => (
            <Toggle key={g.key} compact={compact} on={groups.includes(g.key)} onClick={() => toggleGroup(g.key)}>
              <span style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
                <ShapeIcon shape={g.shape} color="currentColor" />{g.label}
              </span>
            </Toggle>
          ))}
          <Divider />
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: C.textMid }}>
            TAIL
            <input type="range" min={TAIL_MIN} max={TAIL_MAX} value={tail} aria-label="Tail length"
                   onChange={(e) => setTail(Number(e.target.value))} style={{ width: 120, minWidth: 0 }} />
            <span style={{ color: C.text, minWidth: 52 }}>{unit(period, tail)}</span>
          </label>
          <Divider />
          <BarLabel>LABELS</BarLabel>
          {LABEL_MODES.map((m) => (
            <Toggle key={m} compact={compact} on={mode === m} onClick={() => setLabelMode(m)}>{m.toUpperCase()}</Toggle>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
          <div style={{ flex: "1 1 220px", maxWidth: phone ? "none" : 360, minWidth: 0, marginBottom: -8 }}>
            <SearchBox value={query} onChange={setQuery} placeholder="Find a fund (ticker or name)…" label="Search funds" />
          </div>
          <Toggle compact={compact} on={isolateSearch} onClick={() => setIsolateSearch((v) => !v)}>ISOLATE MATCHES</Toggle>
          <button onClick={clearAll} title="Clear the selection, isolation and search" style={{
            ...s.btn("ghost"), minHeight: compact ? 30 : 36, padding: compact ? "4px 8px" : "6px 10px", fontSize: 11,
            opacity: active ? 1 : 0.45,
          }}>CLEAR</button>
          <span style={{ fontSize: 11, color: C.textDim, flex: "1 1 260px", minWidth: 0 }}>{RSP_NOTE}</span>
        </div>
      </div>

      {status === "loading" || !data ? (
        <div style={{ padding: 16, color: C.textDim, fontSize: 12 }}>LOADING…</div>
      ) : (
        <div>
          <div data-layout={box.wide ? "wide" : phone ? "phone" : "stacked"}
               style={{ display: "flex", gap: 16, alignItems: "flex-start", flexDirection: box.wide ? "row" : "column" }}>
            <div ref={setPlotEl} style={{ flex: box.wide ? "1 1 auto" : "none", minWidth: 0, width: box.wide ? undefined : "100%" }}>
              <RotationPlot items={items} period={period} tail={tail} w={box.w} h={box.h} phone={phone}
                            highlight={highlight} selected={selected} labelMode={mode}
                            hover={hover} pinned={pinned} onHover={setHover} onPin={onPin} />
            </div>
            {box.wide ? (
              <aside data-panel style={{ flex: `0 0 ${PANEL_W}px`, width: PANEL_W, maxHeight: box.h, overflowY: "auto" }}>
                {list}
              </aside>
            ) : null}
          </div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>{DRIFT_NOTE}</div>
          {phone ? (
            <div style={{ marginTop: 10 }}>
              {QUAD_ORDER.map((qk) => {
                const rows = all.filter((it) => it.quadrant === qk);
                if (!rows.length) return null;
                return (
                  <div key={qk} data-list={qk} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: QUAD[qk].color, letterSpacing: "0.1em", marginBottom: 4 }}>
                      {QUAD[qk].label}
                    </div>
                    {rows.map((it) => (
                      <button key={it.symbol} data-row={it.symbol} onClick={() => toggleIsolate(it.symbol)} style={{
                        display: "flex", width: "100%", minHeight: 40, gap: 8, alignItems: "center",
                        background: isolated === it.symbol ? C.panel : "none", border: "none",
                        borderBottom: `1px solid ${C.border}`, color: C.text, fontFamily: font, fontSize: 12,
                        textAlign: "left", padding: "4px 2px", cursor: "pointer",
                      }}>
                        <span style={{ fontWeight: 700, minWidth: 44 }}>{it.symbol}</span>
                        <span style={{ flex: 1, minWidth: 0, color: C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</span>
                        {Number.isInteger(it.periods_in_quadrant) && it.periods_in_quadrant > 0 ? (
                          <span style={{ color: C.textDim, fontSize: 11 }}>{unit(period, it.periods_in_quadrant)}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
              {legendGroups.map((g) => {
                const rows = all.filter((it) => it.group === g.key);
                if (!rows.length) return null;
                return (
                  <div key={g.key} style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ display: "inline-flex", gap: 5, alignItems: "center", fontSize: 10, color: C.textDim, minWidth: 104, letterSpacing: "0.1em" }}>
                      <ShapeIcon shape={g.shape} color={C.textMid} />{g.label}
                    </span>
                    {rows.map((it) => (
                      <button key={it.symbol} data-legend={it.symbol} onClick={() => toggleIsolate(it.symbol)}
                              onPointerEnter={(e) => { if (e.pointerType !== "touch") setHover(it.symbol); }}
                              onPointerLeave={(e) => { if (e.pointerType !== "touch") setHover(null); }}
                              title={`${it.name} — click to isolate`} style={{
                        background: isolated === it.symbol ? C.panel : "none", border: `1px solid ${C.border}`,
                        borderRadius: 3, color: QUAD[it.quadrant].color, fontFamily: font, fontSize: 11,
                        padding: "2px 6px", cursor: "pointer", minHeight: 24,
                      }}>{it.symbol}</button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
          {!phone && !box.wide ? <div style={{ marginTop: 10 }}>{list}</div> : null}
          {missing.length ? (
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 8 }}>
              Not shown: {missing.map((m) => `${m.symbol} (${m.reason})`).join("; ")}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function RotationMapTab() {
  const [benchmark, setBenchmark] = useState("SPY");
  const [period, setPeriod] = useState("daily");
  const [state, setState] = useState({ status: "loading", data: null });
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setState({ status: "loading", data: null });
    fetchRotation(benchmark, period)
      .then((d) => { if (seq.current === mine) setState(d ? { status: "ready", data: d } : { status: "unavailable", data: null }); })
      .catch(() => { if (seq.current === mine) setState({ status: "unavailable", data: null }); });
  }, [benchmark, period]);

  return (
    <RotationMapView status={state.status} data={state.data} benchmark={benchmark} period={period}
                     onBenchmark={setBenchmark} onPeriod={setPeriod} />
  );
}
